import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { Job, JobRow } from "../types";
import { jobRowToJob, jobToRow } from "../types";
import { uuid } from "../utils/uuid";

export function useJobs(ownerId?: string) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // MULTI-TENANT: Ne pas charger tous les jobs si ownerId pas encore connu (évite afficher les données des autres clients au refresh)
      if (!ownerId) {
        setJobs([]);
        setLoading(false);
        return;
      }
      const { data, error: e } = await supabase
        .from("jobs")
        .select("*")
        .eq("owner_id", ownerId)
        .order("created_at", { ascending: false });
      if (e) throw e;
      const rows = (data ?? []) as JobRow[];
      setJobs(rows.map(jobRowToJob));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur chargement jobs");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const create = useCallback(async (payload: Omit<Job, "id" | "createdAt">) => {
    try {
      const id = uuid();
      const createdAt = new Date().toISOString();
      const baseRow = jobToRow({ ...payload, id, createdAt });
      
      // MULTI-TENANT: Ajouter owner_id seulement s'il est défini
      const row: Record<string, unknown> = { 
        ...baseRow, 
        id, 
        created_at: createdAt,
      };
      
      if (ownerId) row.owner_id = ownerId;
      
      const { data, error: e } = await supabase.from("jobs").insert(row).select().single();
      if (e) throw e;
      const job = jobRowToJob(data as JobRow);
      setJobs((prev) => [job, ...prev]);
      return { data: job, error: null };
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : (err && typeof err === "object" && "message" in err)
            ? String((err as { message: unknown }).message)
            : "Erreur création job";
      return { data: null, error: msg || "Erreur création job" };
    }
  }, [ownerId]);

  const update = useCallback(async (job: Job) => {
    try {
      const row = jobToRow(job);
      const { data, error: e } = await supabase
        .from("jobs")
        .update(row)
        .eq("id", job.id)
        .select()
        .single();
      if (e) throw e;
      const updated = jobRowToJob(data as JobRow);
      setJobs((prev) => prev.map((j) => (j.id === job.id ? updated : j)));
      return { data: updated, error: null };
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err.message : "Erreur mise à jour job",
      };
    }
  }, []);

  const remove = useCallback(async (id: string) => {
    try {
      const { error: e } = await supabase.from("jobs").delete().eq("id", id);
      if (e) throw e;
      setJobs((prev) => prev.filter((j) => j.id !== id));
      return { error: null };
    } catch (err) {
      return {
        error: err instanceof Error ? err.message : "Erreur suppression job",
      };
    }
  }, []);

  return { jobs, loading, error, refetch: fetchAll, create, update, remove };
}
